import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import ScienceIcon from "@mui/icons-material/Science";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AccountInfoBlock,
  ActivationFunnelBlock,
  AnalyticsHeader,
  AssignmentPipelineBlock,
  HeadlineFiguresBlock,
  ReadingBySchoolTable,
  ReadingMixBlock,
  ReadingTrendsBlock,
  TopBooksBlock,
  AnalyticsHeaderSkeleton,
  RangeCaption,
  RangeCaptionSkeleton,
  RangeFilterBar,
  RangeFilterBarSkeleton,
} from "shared/components/schoolAnalytics";
import type {
  AccountMetric,
  BlockToggle,
  FunnelView,
  HeadlineFigure,
  CustomRange,
  RangePreset,
} from "shared/components/schoolAnalytics";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import {
  ACCOUNT_FILTERS,
  ACCOUNT_METRIC_DEFS,
  BOOK_SORTS,
  MIX_DIMENSIONS,
  STATUS_FILTERS,
  TREND_METRICS,
  toBands,
  toBookRows,
  toFunnelStages,
  toHeaderStats,
  toMixSlices,
  toPipelineCaption,
  toPipelineSlices,
  toRankRows,
  toSilentSchools,
  toTrend,
  trendDelta,
} from "./adapters";
import { fmt, isRangeReady, resolveRange } from "./data";
import useSchoolAnalytics from "./useSchoolAnalytics";
import styles from "./style.module.scss";

const PRESETS = [
  { key: 7 as RangePreset, label: "Last 7 days" },
  { key: 30 as RangePreset, label: "Last 30 days" },
  { key: "term" as RangePreset, label: "This term" },
  { key: "custom" as RangePreset, label: "Custom" },
];

const BLOCK_DEFS = [
  { key: "account", label: "Account information" },
  { key: "headline", label: "Headline figures" },
  { key: "funnel", label: "Activation funnel" },
  { key: "trends", label: "Reading growth trends" },
  { key: "pipeline", label: "Assignment pipeline" },
  { key: "mix", label: "Reading mix" },
  { key: "books", label: "Top books" },
  { key: "bySchool", label: "Reading by school" },
];

/** One icon per account count, keyed by the dashboard payload's own field. */
const ACCOUNT_ICONS: Record<string, ReactNode> = {
  demo_accounts: <HourglassTopIcon />,
  live_premium_accounts: <WorkspacePremiumIcon />,
  expired_premium_accounts: <EventBusyIcon />,
  expiring_premium_accounts: <NotificationsActiveIcon />,
  renewed_premium_accounts: <AutorenewIcon />,
  test_accounts: <ScienceIcon />,
};

const RANK_PAGE_SIZE = 10;

interface SchoolAnalyticsProps {
  /** Rendered inside another screen's tab (Product Manager's School Users),
   *  which owns the breadcrumb, so this page must not replace it. */
  embedded?: boolean;
}

/**
 * School Analytics — Project Head, and the Product Manager's "School
 * Analytics" tab on School Users (same screen, same endpoints).
 *
 * The page owns all state and fetching; every block under
 * shared/components/schoolAnalytics is headless and driven from here.
 */
export function SchoolAnalyticsView({ embedded = false }: SchoolAnalyticsProps) {
  // An empty crumb is a no-op in useBreadcrumbs.
  useBreadcrumbs(
    embedded
      ? {}
      : {
          title: routeConstant.schoolAnalytics.title,
          path: routeConstant.schoolAnalytics.path,
        }
  );

  const [preset, setPreset] = useState<RangePreset>(30);
  // Starts empty on purpose: picking "Custom" must not fetch anything until
  // the user has chosen both ends of the range themselves.
  const [customRange, setCustomRange] = useState<CustomRange>({
    from: "",
    to: "",
  });
  const [customizeOpen, setCustomizeOpen] = useState<boolean>(false);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [openBand, setOpenBand] = useState<string | null>(null);
  const [funnelView, setFunnelView] = useState<FunnelView>("bars");
  const [mixDimension, setMixDimension] = useState<string>("genres");
  const [bookSort, setBookSort] = useState<string>("by_read");
  const [trendMetric, setTrendMetric] = useState<string>("book_read");
  const [trendMenuOpen, setTrendMenuOpen] = useState<boolean>(false);
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rankPage, setRankPage] = useState<number>(0);

  const navigate = useNavigate();
  const openSchool = (id: number | string) =>
    navigate(`${routeConstant.schoolAnalytics.path}/${id}`);

  const rangeReady = isRangeReady(preset, customRange);

  /** Choosing "Custom" clears the pair, so the screen waits for a fresh pick. */
  const changePreset = (next: RangePreset) => {
    if (next === "custom") setCustomRange({ from: "", to: "" });
    setPreset(next);
  };

  const range = useMemo(
    () => resolveRange(preset, customRange),
    [preset, customRange]
  );

  const prevShort = range.prevShort;

  const {
    core,
    coreLoading,
    books,
    booksLoading,
    trend,
    trendLoading,
    reader,
    readerLoading,
  } = useSchoolAnalytics({
    from: range.from,
    to: range.to,
    bookSort,
    trendMetric,
    accountFilter,
    statusFilter,
    rankPage,
    rankPageSize: RANK_PAGE_SIZE,
    enabled: rangeReady,
  });

  const headerStats = useMemo(
    () => toHeaderStats(core.overall),
    [core.overall]
  );

  const accountMetrics: AccountMetric[] = useMemo(() => {
    const stats = core.dashboard;
    if (!stats) return [];

    return ACCOUNT_METRIC_DEFS.map((def) => {
      const figure = stats[def.key];
      return {
        key: def.key,
        label: def.label,
        value: figure?.value ?? 0,
        unit: def.unit,
        icon: ACCOUNT_ICONS[def.key],
        delta: trendDelta(figure?.trend, prevShort, def.invert),
      };
    });
  }, [core.dashboard, prevShort]);

  const bands = useMemo(() => toBands(core.health), [core.health]);

  const figures: HeadlineFigure[] = useMemo(() => {
    const { regular, level, silent } = core;
    const list: HeadlineFigure[] = [];

    if (regular) {
      const enrolled = regular.enrolled_students ?? 0;
      const readers = regular.regular_readers?.value ?? 0;
      list.push({
        key: "regular",
        label: "Regular readers",
        value: fmt(readers),
        unit: `${regular.regular_readers?.percentage ?? 0}% of ${fmt(
          enrolled
        )} enrolled`,
        icon: <MenuBookIcon />,
        delta: trendDelta(regular.regular_readers?.trend, prevShort),
        definition:
          "Regular reader: read on every day of the selected date range.",
        ring: {
          ratio: enrolled ? readers / enrolled : 0,
          legendA: `${fmt(readers)} regular`,
          legendB: `${fmt(Math.max(0, enrolled - readers))} not yet`,
        },
      });
    }

    if (silent) {
      const schools = toSilentSchools(silent);
      list.push({
        key: "silent",
        label: "Schools with nobody assigning",
        value: silent.count ?? schools.length,
        unit: "schools",
        icon: <PersonOffIcon />,
        definition:
          "No teacher at the school assigned a single book inside the selected date range.",
        alertSchools: schools,
      });
    }

    if (level) {
      const enrolled = level.enrolled_students ?? 0;
      const movedUp = level.moved_up?.value ?? 0;
      list.push({
        key: "levelUps",
        label: "Reading levels moved up",
        value: fmt(movedUp),
        unit: `${level.moved_up?.percentage ?? 0}% of ${fmt(
          enrolled
        )} enrolled`,
        icon: <TrendingUpIcon />,
        delta: trendDelta(level.moved_up?.trend, prevShort),
        definition: `${fmt(level.same_level ?? 0)} students stayed on the same level.`,
        ring: {
          ratio: enrolled ? movedUp / enrolled : 0,
          legendA: `${fmt(movedUp)} moved up`,
          legendB: `${fmt(level.same_level ?? 0)} same level`,
        },
      });
    }

    return list;
  }, [core, prevShort]);

  const funnelStages = useMemo(
    () => toFunnelStages(core.funnel, prevShort),
    [core.funnel, prevShort]
  );

  const mixSlices = useMemo(
    () => toMixSlices(core.mix, mixDimension),
    [core.mix, mixDimension]
  );

  const pipelineStages = useMemo(
    () => toPipelineSlices(core.assignment),
    [core.assignment]
  );

  const bookRows = useMemo(() => toBookRows(books), [books]);

  const trendData = useMemo(
    () => toTrend(trend, trendMetric),
    [trend, trendMetric]
  );

  const rankRows = useMemo(
    () => toRankRows(reader, prevShort),
    [reader, prevShort]
  );

  const blocks: BlockToggle[] = BLOCK_DEFS.map((block) => ({
    ...block,
    visible: !hidden[block.key],
  }));

  return (
    <div className={styles.page}>
      {coreLoading ? (
        <>
          <AnalyticsHeaderSkeleton />
          <RangeFilterBarSkeleton />
          <RangeCaptionSkeleton />
        </>
      ) : (
        <>
          <AnalyticsHeader
            title="All schools"
            stats={headerStats}
            actions={
              <RangeFilterBar
                presets={PRESETS}
                preset={preset}
                onPresetChange={changePreset}
                showCustomRange={preset === "custom"}
                range={customRange}
                onRangeChange={setCustomRange}
                blocks={blocks}
                customizeOpen={customizeOpen}
                onCustomizeOpenChange={setCustomizeOpen}
                onToggleBlock={(key, visible) =>
                  setHidden((prev) => ({ ...prev, [key]: !visible }))
                }
              />
            }
          />
          <RangeCaption
            range={range.rangeLabel}
            comparedWith={range.prevLabel}
          />
        </>
      )}

      <div className={styles.grid}>
        {!hidden.account && (
          <AccountInfoBlock
            metrics={accountMetrics}
            loading={coreLoading}
            empty={!accountMetrics.length}
            className={styles.blockAccount}
          />
        )}

        {!hidden.headline && (
          <HeadlineFiguresBlock
            bands={bands}
            figures={figures}
            openBand={openBand}
            onToggleBand={(key) =>
              setOpenBand((current) => (current === key ? null : key))
            }
            onSelectSchool={openSchool}
            bandDefinition="Healthy: health score 70 and above. Slipping: 40 to 69. Dead: below 40."
            loading={coreLoading}
            empty={!bands.length && !figures.length}
            className={styles.blockHeadline}
          />
        )}

        {!hidden.funnel && (
          <ActivationFunnelBlock
            stages={funnelStages}
            view={funnelView}
            onViewChange={setFunnelView}
            format={fmt}
            loading={coreLoading}
            empty={!funnelStages.length}
            className={styles.blockFunnel}
          />
        )}

        {!hidden.mix && (
          <ReadingMixBlock
            slices={mixSlices}
            dimensions={MIX_DIMENSIONS}
            dimension={mixDimension}
            onDimensionChange={setMixDimension}
            loading={coreLoading}
            empty={!mixSlices.length}
            className={styles.blockMix}
          />
        )}

        {!hidden.books && (
          <TopBooksBlock
            books={bookRows}
            sorts={BOOK_SORTS}
            sort={bookSort}
            onSortChange={setBookSort}
            loading={booksLoading}
            empty={!bookRows.length}
            className={styles.blockBooks}
          />
        )}

        {!hidden.pipeline && (
          <AssignmentPipelineBlock
            stages={pipelineStages}
            centerCaption={toPipelineCaption(core.assignment)}
            loading={coreLoading}
            empty={!pipelineStages.length}
            className={styles.blockPipeline}
          />
        )}

        {!hidden.trends && (
          <ReadingTrendsBlock
            series={trendData.series}
            legend={trendData.legend}
            labels={trendData.labels}
            metrics={TREND_METRICS}
            metric={trendMetric}
            onMetricChange={setTrendMetric}
            menuOpen={trendMenuOpen}
            onMenuOpenChange={setTrendMenuOpen}
            loading={trendLoading}
            empty={!trendData.labels.length}
            className={styles.blockTrends}
          />
        )}

        {!hidden.bySchool && (
          <ReadingBySchoolTable
            rows={rankRows}
            page={rankPage}
            pageSize={RANK_PAGE_SIZE}
            total={reader?.pagination?.total ?? 0}
            onPageChange={setRankPage}
            accountOptions={ACCOUNT_FILTERS}
            accountFilter={accountFilter}
            onAccountFilterChange={(key) => {
              setAccountFilter(key);
              setRankPage(0);
            }}
            statusFilters={STATUS_FILTERS}
            statusFilter={statusFilter}
            onStatusFilterChange={(key) => {
              setStatusFilter(key);
              setRankPage(0);
            }}
            onSelectSchool={openSchool}
            loading={readerLoading}
            empty={!rankRows.length}
            className={styles.blockBySchool}
          />
        )}
      </div>
    </div>
  );
}

/** The route component: route entries take no props. */
function SchoolAnalytics() {
  return <SchoolAnalyticsView />;
}

export default SchoolAnalytics;
